/**
 * Shared site identity graph, used by homepage, articles, and design pages.
 * Plain fields keep this independent of the build environment.
 */
export type HomePageJsonLdFields = {
  siteUrl: string
  siteName: string
  description: string
  authorName: string
  authorTitle: string
  linkedInUrl: string
  organizationLogoUrl: string
}

export const buildHomePageJsonLd = (fields: HomePageJsonLdFields): Record<string, unknown> => {
  const baseSiteUrl = fields.siteUrl.replace(/\/+$/, '')
  const joinUrl = (pathname: string) => new URL(pathname, `${baseSiteUrl}/`).toString()

  const ids = {
    organization: `${baseSiteUrl}#organization`,
    website: `${baseSiteUrl}#website`,
    person: `${baseSiteUrl}#person`,
  }

  const organizationLogoAbsolute = (): string => {
    const raw = fields.organizationLogoUrl.trim()
    if (!raw) {
      return joinUrl('/favicon.svg')
    }
    return /^https?:\/\//i.test(raw) ? raw : joinUrl(raw.startsWith('/') ? raw : `/${raw}`)
  }

  const imageObject = (url: string) => ({
    '@type': 'ImageObject',
    url,
  })

  const personSameAs = (): string[] => {
    const li = fields.linkedInUrl.trim()
    if (li.includes('linkedin.com/in/')) {
      return [li]
    }
    return []
  }

  const graph: Record<string, unknown>[] = [
    {
      '@type': 'Organization',
      '@id': ids.organization,
      name: fields.siteName,
      url: baseSiteUrl,
      logo: imageObject(organizationLogoAbsolute()),
    },
    {
      '@type': 'WebSite',
      '@id': ids.website,
      name: fields.siteName,
      url: baseSiteUrl,
      description: fields.description,
      publisher: { '@id': ids.organization },
    },
  ]

  const personNode: Record<string, unknown> = {
    '@type': 'Person',
    '@id': ids.person,
    name: fields.authorName,
    jobTitle: fields.authorTitle,
  }
  const sameAs = personSameAs()
  if (sameAs.length) {
    personNode.sameAs = sameAs
  }
  graph.push(personNode)

  return {
    '@context': 'https://schema.org',
    '@graph': graph,
  }
}
